import { useContext, useEffect, ReactElement, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useOrganizationId } from '@app/utils/hooks';
import { useStore } from '@data/storages';
import { useOrganizations } from '@data/hooks/organizations';
import { ACCESS_TOKEN_STORAGE_KEY } from '@app/constants/app/storage';
import type { DecodedJwt } from '../types';
import AuthContext from './context';

interface AuthProps {
  children?: ReactElement;
  redirectTo?: string | null;
  preloader?: ReactNode;
  allowedRole?: string | null;
  allowedRoles?: (string | undefined)[] | null;
}

const AuthorizedComponent = ({
  children,
  redirectTo,
  preloader,
  allowedRole,
  allowedRoles,
}: AuthProps): ReactElement | ReactNode => {
  const { redirect, getLocation } = useContext(AuthContext);
  const orgIdFromRoute = useOrganizationId();
  const { organization } = useStore();

  const tokenFromStorage = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  let userId: string | undefined;
  if (tokenFromStorage) {
    const decoded = jwtDecode<DecodedJwt>(tokenFromStorage);
    userId = decoded.userId;
  }

  const { data: organizations, isPending } = useOrganizations(userId);
  const orgIdForRole = orgIdFromRoute ?? organization?.id;
  const role = organizations?.items.find(org => org.id === orgIdForRole)?.role as string | undefined;

  const hasAccess = allowedRoles ? allowedRoles.includes(role as string) : role === allowedRole;

  useEffect(() => {
    if (!redirectTo || hasAccess) {
      return;
    }
    if (!userId || isPending) {
      return;
    }
    redirect(redirectTo, { location: getLocation() });
  }, [hasAccess, redirectTo, redirect, getLocation, isPending, userId]);

  if (hasAccess) {
    return children;
  }

  return preloader;
};

export default AuthorizedComponent;
